import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    style?: React.CSSProperties;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange, style }) => {
    const isMobile = useIsMobile();

    if (totalPages <= 1) return null;

    const isFirst = currentPage <= 1;
    const isLast = currentPage >= totalPages;
    const safeGo = (page: number) => {
        const next = Math.max(1, Math.min(totalPages, page));
        if (next !== currentPage) onPageChange(next);
    };

    const buttonStyle = (disabled: boolean): React.CSSProperties => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 'var(--space-sm)' : 'var(--space-md)',
        borderRadius: 'var(--radius-xs)',
        backgroundColor: disabled ? 'transparent' : 'var(--row-hover)',
        border: '1px solid var(--app-divider)',
        color: disabled ? 'var(--app-text-secondary)' : 'var(--app-text-primary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
    });

    return (
        <nav
            aria-label="Pagination"
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? 'var(--space-sm)' : 'var(--space-lg)',
                padding: isMobile ? 'var(--space-xl) var(--space-lg)' : 'var(--space-2xl)',
                ...style,
            }}
        >
            {!isMobile && (
                <button
                    type="button"
                    onClick={() => safeGo(1)}
                    disabled={isFirst}
                    style={buttonStyle(isFirst)}
                    aria-label="First page"
                    title="First page"
                >
                    <ChevronsLeft size={16} />
                </button>
            )}

            <button
                type="button"
                onClick={() => safeGo(currentPage - 1)}
                disabled={isFirst}
                style={buttonStyle(isFirst)}
                aria-label="Previous page"
                title="Previous page"
            >
                <ChevronLeft size={isMobile ? 14 : 16} />
            </button>

            <div
                style={{
                    color: 'var(--app-text-secondary)',
                    fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-lg)',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                }}
            >
                <span style={{ color: 'var(--app-text-primary)', fontWeight: 'bold' }}>{currentPage}</span>
                <span> / {totalPages}</span>
            </div>

            <button
                type="button"
                onClick={() => safeGo(currentPage + 1)}
                disabled={isLast}
                style={buttonStyle(isLast)}
                aria-label="Next page"
                title="Next page"
            >
                <ChevronRight size={isMobile ? 14 : 16} />
            </button>

            {!isMobile && (
                <button
                    type="button"
                    onClick={() => safeGo(totalPages)}
                    disabled={isLast}
                    style={buttonStyle(isLast)}
                    aria-label="Last page"
                    title="Last page"
                >
                    <ChevronsRight size={16} />
                </button>
            )}
        </nav>
    );
};

export default Pagination;
